import { useState, useEffect } from 'react';
import RawFilePreview from '@/components/common/RawFilePreview';
import { SystemEntity } from '@erp/shared-types';
import type { GetDocumentPreviewResponse } from '@erp/rpc';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { SystemEntityPreview } from '@/components/system-entities/preview-ui';
import { getEntityDisplayName } from '@/components/system-entities/utils';
import { EmailViewerV2 } from '@/components/email-viewer/email-viewer-v2';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { InvoiceDetails } from '@/components/accounts-payable/invoice-detail/invoice-details';
import {
  FLOW_IN_SOCKET_ID,
  FLOW_OUT_SOCKET_ID,
  SOCKET_NOT_TAKEN,
} from '@erp/workflows';
import { T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import {
  useDocumentOcrResultContent,
  useDocumentOcrResultPreview,
  useDocumentPreview,
} from '@/api/rpc/hooks/core/useDocuments';
import {
  useAPInvoice,
  useAPInvoiceExtractionResult,
} from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import useGetInboundEmail from '@/api/core/inbound-emails/getInboundEmail';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContentResizable,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';

interface StepArtifactsDisplayProps {
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  className?: string;
}

function isEntityReference(
  value: unknown,
): { entityType: SystemEntity; entityId: string } | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (
      typeof obj.entityType === 'string' &&
      typeof obj.entityId === 'string' &&
      Object.values(SystemEntity).includes(obj.entityType as SystemEntity)
    ) {
      return {
        entityType: obj.entityType as SystemEntity,
        entityId: obj.entityId as string,
      };
    }
  }
  return null;
}

function isSimpleType(value: unknown): boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  );
}

function formatSimpleValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasNestedEntityRefs(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some(
    (v) =>
      isEntityReference(v) !== null ||
      (Array.isArray(v) && hasNestedEntityRefsInArray(v)) ||
      (isPlainObject(v) && hasNestedEntityRefs(v)),
  );
}

function hasNestedEntityRefsInArray(items: unknown[]): boolean {
  return items.some(
    (item) =>
      isEntityReference(item) !== null ||
      (Array.isArray(item) && hasNestedEntityRefsInArray(item)) ||
      (isPlainObject(item) && hasNestedEntityRefs(item)),
  );
}

type DocumentPreviewMetadata = GetDocumentPreviewResponse['metadata'];

type DocumentPreviewData = GetDocumentPreviewResponse;

function DocumentMetadataPanel({
  metadata,
}: {
  metadata?: DocumentPreviewMetadata;
}) {
  if (!metadata) return null;

  return (
    <div className="mb-2 rounded-md border bg-muted/30 p-2 text-xs space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {metadata.isSplitParent && (
          <span className="rounded border px-1.5 py-0.5">
            <T>
              Split Parent (<Var>{metadata.childDocumentsCount}</Var>)
            </T>
          </span>
        )}
        {metadata.isSplitChild && (
          <span className="rounded border px-1.5 py-0.5">
            <T context="badge label for a split child document">Split Child</T>
          </span>
        )}
        {metadata.isTransformedFile && (
          <span className="rounded border px-1.5 py-0.5">
            <T context="badge label for a transformed file document">
              Transformed File
            </T>
          </span>
        )}
      </div>
      {metadata.classificationType && (
        <div>
          <span className="text-muted-foreground">
            <T context="label for document classification type">
              Classification:{' '}
            </T>
          </span>
          <span className="font-medium">{metadata.classificationType}</span>
        </div>
      )}
      {typeof metadata.classificationConfidence === 'number' && (
        <div>
          <span className="text-muted-foreground">
            <T context="label for document classification confidence score">
              Confidence:{' '}
            </T>
          </span>
          <span className="font-medium">
            {Math.round(metadata.classificationConfidence * 100)}%
          </span>
        </div>
      )}
      {metadata.classificationMethod && (
        <div>
          <span className="text-muted-foreground">
            <T context="label for document classification method">Method: </T>
          </span>
          <span className="font-medium">{metadata.classificationMethod}</span>
        </div>
      )}
      {metadata.parentDocumentId && (
        <div>
          <span className="text-muted-foreground">
            <T context="label for parent document reference">Parent: </T>
          </span>
          <span className="font-medium font-mono">
            {metadata.parentDocumentId.slice(0, 8)}
          </span>
        </div>
      )}
    </div>
  );
}

function SharedDocumentFullView({ data }: { data: DocumentPreviewData }) {
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPreviewFile(null);
    setPreviewError(null);
    fetch(data.signedUrl)
      .then(async (res) => {
        if (!res.ok)
          throw new Error(`Failed to fetch document (${res.status})`);
        const blob = await res.blob();
        if (cancelled) return;
        const file = new File([blob], data.fileName, {
          type: blob.type || 'application/octet-stream',
        });
        setPreviewFile(file);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(
          err instanceof Error ? err.message : 'Failed to load preview',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [data.signedUrl, data.fileName]);

  const handleDownload = async () => {
    try {
      const blob = previewFile
        ? previewFile
        : await fetch(data.signedUrl).then(async (r) => {
            if (!r.ok) {
              throw new Error(`Failed to download document (${r.status})`);
            }
            return r.blob();
          });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = data.fileName;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(data.signedUrl, '_blank');
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-medium truncate">{data.fileName}</div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDownload}
        >
          <T context="button label to download a document">Download</T>
        </Button>
      </div>
      <DocumentMetadataPanel metadata={data.metadata} />
      <div className="flex-1 min-h-0 overflow-auto">
        {previewError ? (
          <div className="text-sm text-destructive">{previewError}</div>
        ) : !previewFile ? (
          <div className="text-sm text-muted-foreground">
            <T context="loading message while fetching document bytes for preview">
              Loading preview...
            </T>
          </div>
        ) : (
          <RawFilePreview
            selectedFiles={[previewFile]}
            showCard={false}
            previewHeight="calc(100vh - 220px)"
          />
        )}
      </div>
    </div>
  );
}

export function FullDocumentPreviewById({
  documentId,
  loadingLabel = 'Loading document...',
}: {
  documentId: string;
  loadingLabel?: string;
}) {
  const { data, isLoading, error } = useDocumentPreview(documentId);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">{loadingLabel}</div>;
  }

  if (error || !data) {
    return (
      <div className="text-sm text-destructive">
        {error instanceof Error ? (
          error.message
        ) : (
          <T context="error message when document preview cannot be loaded">
            Unable to load document preview.
          </T>
        )}
      </div>
    );
  }

  return <SharedDocumentFullView data={data} />;
}

function FullDocumentEntityView({ entityId }: { entityId: string }) {
  return <FullDocumentPreviewById documentId={entityId} />;
}

function FullOcrEntityView({ entityId }: { entityId: string }) {
  const gt = useGT();
  const {
    data: fullContent,
    isLoading: isLoadingContent,
    error: contentError,
  } = useDocumentOcrResultContent(entityId, true);
  const { value: ocrTopPanelSize, set: setOcrTopPanelSize } =
    useLocalStorageValue<number>('workflow-artifact-ocr-top-panel-size', {
      defaultValue: 50,
    });

  const documentId = fullContent?.documentId ?? null;

  return (
    <ResizablePanelGroup direction="vertical" className="h-full min-h-0 gap-2">
      <ResizablePanel
        defaultSize={ocrTopPanelSize}
        onResize={(size) => setOcrTopPanelSize(size)}
        minSize={20}
        className="min-h-0 rounded-md border p-3"
      >
        <div className="h-full min-h-0">
          {isLoadingContent ? (
            <div className="text-sm text-muted-foreground">
              <T context="loading message while resolving the source document for an OCR result">
                Resolving source document...
              </T>
            </div>
          ) : contentError ? (
            <div className="text-sm text-destructive">
              {contentError instanceof Error ? (
                contentError.message
              ) : (
                <T context="error message when OCR metadata cannot be loaded">
                  Failed to load OCR metadata.
                </T>
              )}
            </div>
          ) : !documentId ? (
            <div className="text-sm text-destructive">
              <T context="error message when OCR result is missing source document ID">
                Source document ID missing from OCR result.
              </T>
            </div>
          ) : (
            <FullDocumentPreviewById
              documentId={documentId}
              loadingLabel="Loading source document..."
            />
          )}
        </div>
      </ResizablePanel>

      <ResizableHandle className="!h-1 !w-full !cursor-row-resize" />

      <ResizablePanel
        minSize={20}
        className="min-h-0 rounded-md border p-3 flex flex-col"
      >
        <h5 className="text-sm font-semibold mb-2">
          <T context="section heading for the full OCR result panel">
            Full OCR Result
          </T>
        </h5>
        <div className="flex-1 min-h-0">
          {isLoadingContent ? (
            <div className="text-sm text-muted-foreground">
              <T context="loading message while fetching the full OCR result">
                Loading full OCR result...
              </T>
            </div>
          ) : contentError ? (
            <div className="text-sm text-destructive">
              {contentError instanceof Error ? (
                contentError.message
              ) : (
                <T context="error message when full OCR result cannot be loaded">
                  Failed to load full OCR result.
                </T>
              )}
            </div>
          ) : (
            <pre className="h-full overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap break-words">
              {fullContent?.fullResult == null
                ? gt('No OCR result payload found.', {
                    context:
                      'message shown when the OCR result document has no payload data',
                  })
                : typeof fullContent.fullResult === 'string'
                  ? fullContent.fullResult
                  : JSON.stringify(fullContent.fullResult, null, 2)}
            </pre>
          )}
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

function FullInboundEmailEntityView({ entityId }: { entityId: string }) {
  const {
    data: inboundEmail,
    isLoading,
    error,
  } = useGetInboundEmail({ inboundEmailId: entityId });

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        <T context="loading message while fetching an inbound email">
          Loading email...
        </T>
      </div>
    );
  }

  if (error || !inboundEmail) {
    return (
      <div className="text-sm text-destructive">
        {error instanceof Error ? (
          error.message
        ) : (
          <T context="error message when email cannot be loaded">
            Unable to load email.
          </T>
        )}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <EmailViewerV2 emailData={inboundEmailToEmailData(inboundEmail)} />
    </div>
  );
}

function FullAPInvoiceEntityView({ entityId }: { entityId: string }) {
  const {
    invoice: invoiceResponse,
    isLoading,
    error,
  } = useAPInvoice(entityId, true);
  const invoice = invoiceResponse?.invoice ?? null;
  const lineItems = invoiceResponse?.lineItems ?? [];
  const inboundEmail = invoiceResponse?.inboundEmail ?? null;
  const relatedConversations = invoiceResponse?.relatedConversations ?? [];
  const extractedTotalWarning = invoiceResponse?.extractedTotalWarning ?? null;

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        <T context="loading message while fetching an AP invoice">
          Loading invoice...
        </T>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="text-sm text-destructive">
        {error instanceof Error ? (
          error.message
        ) : (
          <T context="error message when AP invoice cannot be loaded">
            Unable to load invoice.
          </T>
        )}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-hidden">
      <InvoiceDetails
        invoice={invoice}
        lineItems={lineItems}
        inboundEmail={inboundEmail}
        relatedConversations={relatedConversations}
        extractedTotalWarning={extractedTotalWarning}
      />
    </div>
  );
}

function FullAPInvoiceExtractionResultEntityView({
  entityId,
}: {
  entityId: string;
}) {
  const { extractionResult, isLoading, error } = useAPInvoiceExtractionResult(
    entityId,
    true,
  );
  const { value: extractionTopPanelSize, set: setExtractionTopPanelSize } =
    useLocalStorageValue<number>(
      'workflow-artifact-ap-invoice-extraction-top-panel-size',
      {
        defaultValue: 50,
      },
    );

  const documentId = extractionResult?.documentId ?? null;

  return (
    <ResizablePanelGroup direction="vertical" className="h-full min-h-0 gap-2">
      <ResizablePanel
        defaultSize={extractionTopPanelSize}
        onResize={(size) => setExtractionTopPanelSize(size)}
        minSize={20}
        className="min-h-0 rounded-md border p-3"
      >
        <div className="h-full min-h-0">
          {isLoading ? (
            <div className="text-sm text-muted-foreground">
              <T context="loading message while resolving the source document for an extraction result">
                Resolving source document...
              </T>
            </div>
          ) : error ? (
            <div className="text-sm text-destructive">
              {error instanceof Error ? (
                error.message
              ) : (
                <T context="error message when extraction result cannot be loaded">
                  Unable to load extraction result.
                </T>
              )}
            </div>
          ) : !documentId ? (
            <div className="text-sm text-destructive">
              <T context="error message when extraction result is missing source document ID">
                Source document ID missing from extraction result.
              </T>
            </div>
          ) : (
            <FullDocumentPreviewById
              documentId={documentId}
              loadingLabel="Loading source document..."
            />
          )}
        </div>
      </ResizablePanel>

      <ResizableHandle className="!h-1 !w-full !cursor-row-resize" />

      <ResizablePanel
        minSize={20}
        className="min-h-0 rounded-md border p-3 flex flex-col"
      >
        <h5 className="text-sm font-semibold mb-2">
          <T context="section heading for the AP invoice extraction result panel">
            AP Invoice Extraction Result
          </T>
        </h5>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="text-sm text-muted-foreground">
              <T context="loading message while fetching the extraction result">
                Loading extraction result...
              </T>
            </div>
          ) : error || !extractionResult ? (
            <div className="text-sm text-destructive">
              {error instanceof Error ? (
                error.message
              ) : (
                <T context="error message when extraction result cannot be loaded">
                  Unable to load extraction result.
                </T>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  <T context="label for extraction result status field">
                    Status:
                  </T>
                </span>
                <span
                  className={cn(
                    'rounded px-2 py-0.5 text-xs font-medium',
                    extractionResult.status === 'success'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                      : 'bg-destructive/15 text-destructive',
                  )}
                >
                  {extractionResult.status}
                </span>
              </div>

              <div className="text-sm">
                <span className="text-muted-foreground">
                  <T context="label for AI model used in extraction">Model:</T>
                </span>{' '}
                <span className="font-medium">
                  {extractionResult.modelUsed}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                {extractionResult.documentId && (
                  <SystemEntityInline
                    entityType={SystemEntity.CORE_DOCUMENT}
                    entityId={extractionResult.documentId}
                  />
                )}
                {extractionResult.apInvoiceId && (
                  <SystemEntityInline
                    entityType={SystemEntity.AP_INVOICE}
                    entityId={extractionResult.apInvoiceId}
                  />
                )}
                {extractionResult.commInboundEmailId && (
                  <SystemEntityInline
                    entityType={SystemEntity.INBOUND_EMAIL}
                    entityId={extractionResult.commInboundEmailId}
                  />
                )}
              </div>

              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">
                  <T context="section label for extracted data JSON display">
                    Extracted Data
                  </T>
                </div>
                <pre className="max-h-60 overflow-auto rounded border bg-muted/30 p-2 text-xs whitespace-pre-wrap break-words">
                  {JSON.stringify(
                    extractionResult.extractedData ?? null,
                    null,
                    2,
                  )}
                </pre>
              </div>

              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">
                  <T context="section label for raw AI output JSON display">
                    Raw Output
                  </T>
                </div>
                <pre className="max-h-60 overflow-auto rounded border bg-muted/30 p-2 text-xs whitespace-pre-wrap break-words">
                  {JSON.stringify(extractionResult.rawOutput ?? null, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

function ArtifactEntitySheetContent({
  entityType,
  entityId,
}: {
  entityType: SystemEntity;
  entityId: string;
}) {
  if (entityType === SystemEntity.CORE_DOCUMENT) {
    return <FullDocumentEntityView entityId={entityId} />;
  }

  if (entityType === SystemEntity.CORE_DOCUMENT_OCR_RESULT) {
    return <FullOcrEntityView entityId={entityId} />;
  }

  if (entityType === SystemEntity.INBOUND_EMAIL) {
    return <FullInboundEmailEntityView entityId={entityId} />;
  }

  if (entityType === SystemEntity.AP_INVOICE) {
    return <FullAPInvoiceEntityView entityId={entityId} />;
  }

  // TODO: AI_EXTRACTION_RESULT is a generic entity but today only AP invoice
  // extraction produces one, so we always render the AP invoice view. Once
  // more extraction schemas exist, dispatch on the row's schema-definition
  // ref instead of hardcoding this view.
  if (entityType === SystemEntity.AI_EXTRACTION_RESULT) {
    return <FullAPInvoiceExtractionResultEntityView entityId={entityId} />;
  }

  return (
    <div className="h-full min-h-0 w-full flex flex-col">
      <SystemEntityPreview
        entityType={entityType}
        entityId={entityId}
        className="!w-full !max-w-none !min-w-0"
      />
    </div>
  );
}

function ArtifactSheetTitle({
  entityType,
  entityId,
}: {
  entityType: SystemEntity;
  entityId: string;
}) {
  const defaultTitle = `${getEntityDisplayName(entityType)} · ${entityId.slice(0, 8)}`;

  const { data: documentPreview } = useDocumentPreview(
    entityType === SystemEntity.CORE_DOCUMENT ? entityId : null,
  );
  const { data: ocrPreview } = useDocumentOcrResultPreview(
    entityType === SystemEntity.CORE_DOCUMENT_OCR_RESULT ? entityId : null,
  );
  const { data: inboundEmail } = useGetInboundEmail({
    inboundEmailId: entityType === SystemEntity.INBOUND_EMAIL ? entityId : '',
    enabled: entityType === SystemEntity.INBOUND_EMAIL,
  });
  const { invoice: invoiceResponse } = useAPInvoice(
    entityType === SystemEntity.AP_INVOICE ? entityId : undefined,
    entityType === SystemEntity.AP_INVOICE,
  );
  const { extractionResult } = useAPInvoiceExtractionResult(
    entityType === SystemEntity.AI_EXTRACTION_RESULT ? entityId : undefined,
    entityType === SystemEntity.AI_EXTRACTION_RESULT,
  );

  if (entityType === SystemEntity.CORE_DOCUMENT && documentPreview?.fileName) {
    return (
      <span className="truncate">
        {getEntityDisplayName(entityType)} - {documentPreview.fileName}
      </span>
    );
  }

  if (entityType === SystemEntity.CORE_DOCUMENT_OCR_RESULT && ocrPreview) {
    return (
      <span className="truncate">
        <T>
          OCR Result - <Var>{ocrPreview.vendor}</Var> (
          <Var>{ocrPreview.modelVersion}</Var>)
        </T>
      </span>
    );
  }

  if (entityType === SystemEntity.INBOUND_EMAIL && inboundEmail) {
    const subject = inboundEmail.subject || '(No subject)';
    return (
      <span className="truncate">
        {getEntityDisplayName(entityType)} - {subject}
      </span>
    );
  }

  if (entityType === SystemEntity.AP_INVOICE && invoiceResponse?.invoice) {
    const invoiceNumber =
      invoiceResponse.invoice.invoiceNumber || entityId.slice(0, 8);
    return (
      <span className="truncate">
        {getEntityDisplayName(entityType)} - {invoiceNumber}
      </span>
    );
  }

  if (entityType === SystemEntity.AI_EXTRACTION_RESULT && extractionResult) {
    return (
      <span className="truncate">
        {getEntityDisplayName(entityType)} - {extractionResult.status}
      </span>
    );
  }

  return <span className="truncate">{defaultTitle}</span>;
}

function ClickableArtifactEntity({
  entityType,
  entityId,
  className,
}: {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { value: previewPanelSize, set: setPreviewPanelSize } =
    useLocalStorageValue<number>('workflow-artifact-preview-panel-size', {
      defaultValue: 50,
    });

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="inline-block cursor-pointer"
        onClick={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        <SystemEntityInline
          entityType={entityType}
          entityId={entityId}
          className={className}
        />
      </div>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContentResizable
          resizableProps={{
            defaultSize: previewPanelSize,
            onResize: setPreviewPanelSize,
            className: 'flex flex-col',
          }}
        >
          <SheetHeader>
            <SheetTitle>
              <ArtifactSheetTitle entityType={entityType} entityId={entityId} />
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-y-auto pt-3 w-full">
            <ArtifactEntitySheetContent
              entityType={entityType}
              entityId={entityId}
            />
          </div>
        </SheetContentResizable>
      </Sheet>
    </>
  );
}

const ARTIFACT_STRING_SOFT_CAP = 48_000;

export function ArtifactValue({ value }: { value: unknown }) {
  if (value === SOCKET_NOT_TAKEN) {
    return (
      <span className="text-sm text-muted-foreground italic">
        <T context="label shown when a workflow branch was not taken">
          Branch not taken
        </T>
      </span>
    );
  }

  const entityRef = isEntityReference(value);
  if (entityRef) {
    return (
      <ClickableArtifactEntity
        entityType={entityRef.entityType}
        entityId={entityRef.entityId}
      />
    );
  }

  if (typeof value === 'string') {
    const text =
      value.length > ARTIFACT_STRING_SOFT_CAP
        ? `${value.slice(0, ARTIFACT_STRING_SOFT_CAP)}… [truncated at ${ARTIFACT_STRING_SOFT_CAP} chars]`
        : value;
    if (text.length > 160 || text.includes('\n')) {
      return (
        <p className="w-full min-w-0 text-sm text-foreground whitespace-pre-wrap break-words max-h-72 overflow-y-auto rounded border border-border/60 bg-muted/20 px-2 py-1.5">
          {text}
        </p>
      );
    }
    return <span className="text-sm text-foreground break-words">{text}</span>;
  }

  if (Array.isArray(value)) {
    const entityRefs = value
      .map((item) => isEntityReference(item))
      .filter((ref): ref is NonNullable<typeof ref> => ref !== null);

    if (entityRefs.length > 0 && entityRefs.length === value.length) {
      return (
        <div className="flex flex-wrap gap-1">
          {entityRefs.map((ref, i) => (
            <ClickableArtifactEntity
              key={i}
              entityType={ref.entityType}
              entityId={ref.entityId}
            />
          ))}
        </div>
      );
    }

    if (hasNestedEntityRefsInArray(value)) {
      return (
        <div className="space-y-1 pl-2 border-l-2 border-muted">
          {value.map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="text-xs font-mono text-muted-foreground min-w-[60px] shrink-0">
                [{index}]
              </span>
              <div className="flex-1 min-w-0">
                <ArtifactValue value={item} />
              </div>
            </div>
          ))}
        </div>
      );
    }
  }

  if (isSimpleType(value) && typeof value !== 'string') {
    return (
      <span className="text-sm text-foreground">
        {formatSimpleValue(value)}
      </span>
    );
  }

  if (isPlainObject(value) && hasNestedEntityRefs(value)) {
    return (
      <div className="space-y-1 pl-2 border-l-2 border-muted">
        {Object.entries(value).map(([key, nestedValue]) => (
          <div key={key} className="flex items-start gap-2">
            <span className="text-xs font-mono text-muted-foreground min-w-[60px] shrink-0">
              {key}:
            </span>
            <div className="flex-1 min-w-0">
              <ArtifactValue value={nestedValue} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-w-md">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function ArtifactSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown>;
}) {
  const filteredEntries = Object.entries(data).filter(
    ([key]) => key !== FLOW_IN_SOCKET_ID && key !== FLOW_OUT_SOCKET_ID,
  );

  if (filteredEntries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h6 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </h6>
      <div className="space-y-1.5">
        {filteredEntries.map(([key, value]) => (
          <div key={key} className="flex items-start gap-2 min-w-0">
            <span className="text-sm font-mono text-muted-foreground shrink-0 pt-0.5">
              {key}:
            </span>
            <div className="min-w-0 flex-1">
              <ArtifactValue value={value} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StepArtifactsDisplay({
  inputData,
  outputData,
  className,
}: StepArtifactsDisplayProps) {
  const hasInputs =
    inputData &&
    Object.keys(inputData).filter(
      (k) => k !== FLOW_IN_SOCKET_ID && k !== FLOW_OUT_SOCKET_ID,
    ).length > 0;

  const hasOutputs =
    outputData &&
    Object.keys(outputData).filter(
      (k) => k !== FLOW_IN_SOCKET_ID && k !== FLOW_OUT_SOCKET_ID,
    ).length > 0;

  const gt = useGT();

  if (!hasInputs && !hasOutputs) {
    return (
      <p className="text-xs text-muted-foreground italic">
        <T>No artifacts</T>
      </p>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {hasInputs && inputData && (
        <ArtifactSection
          title={gt('Inputs', {
            context: 'section title for workflow step input artifacts',
          })}
          data={inputData}
        />
      )}
      {hasOutputs && outputData && (
        <ArtifactSection
          title={gt('Outputs', {
            context: 'section title for workflow step output artifacts',
          })}
          data={outputData}
        />
      )}
    </div>
  );
}
